import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { LandingProductShots } from "../components/marketing/LandingProductShots";
import { LandingSectionHeading } from "../components/marketing/LandingSectionHeading";
import { MarketingInstrumentDemo } from "../components/marketing/MarketingInstrumentDemo";
import { MarketingSiteFooter } from "../components/marketing/MarketingSiteFooter";
import { LANDING_NAV_SAFE_AREA_CLASS } from "../components/marketing/MarketingNav";
import {
  getSubscriptionDisplayPricing,
  resolveBrowserSubscriptionLocale,
} from "../../shared/subscriptionPricingDisplay.js";
import { fetchPricingFreeFirstBundle } from "../lib/freeFirstUi";
import { WWW_PAGE_SHELL_MIN_H_CLASS } from "../lib/wwwViewportShell";
import { wwwRouteEnter } from "../lib/wwwRouteMotion";
import { useInstantMotion } from "../hooks/useInstantMotion";

export { LANDING_NAV_SAFE_AREA_CLASS };

const LANDING_SURFACE_BAND_CLASS = 'py-10 sm:py-16 bg-[var(--bg-primary)]';
const LANDING_SURFACE_BAND_RAISED_CLASS =
  'py-10 sm:py-16 bg-[var(--surface-raised)]';
const LANDING_SURFACE_BAND_TIGHT_CLASS =
  'relative py-10 sm:py-16 overflow-hidden bg-[var(--bg-primary)]';
const LANDING_PRIMARY_CTA_CLASS =
  'btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5';
const LANDING_SECONDARY_CTA_CLASS =
  'btn-secondary-ghost inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5';
const LANDING_PRICING_CTA_LABEL = 'See pricing';
const LANDING_HERO_PRICING_CTA = 'hero-see-pricing';
const LANDING_BOTTOM_PRICING_CTA = 'bottom-see-pricing';

const FAQ_COST_OPEN =
  "Free includes goal selection, custom training plan generations, personalized meal prep, workout exports, bonk prediction on planned workouts, core nutrition logging, and Intervals.icu ride sync and plan push. Current Free caps are listed on the Pricing page and in your account. Pro adds higher plan and meal-prep caps, unlimited saved plans, unlimited exports, and bonk risk history. Pro pricing is shown in your local currency on the Pricing page (web checkout may still bill in USD; App Store and Play use store prices). Pro includes a 7-day free trial - cancel anytime.";

const FAQ_COST_PAUSED =
  "FuelCyclePro is free during launch with raised Free limits. Paid Pro checkout is paused for now - limits and what is included are listed on the Pricing page. Pro plans and paid checkout will return later.";

const faqItemsBase = [
  {
    question: "How does FuelCyclePro work?",
    answer:
      "Create a personalized training plan based on your goals, FTP, and schedule. For every ride, FuelCyclePro turns the workout into a clear execution plan: what to eat before, what to carry, when to take it, how much to drink, and what to correct after the ride.",
  },
  {
    question: "Does FuelCyclePro help with consistency?",
    answer:
      "Yes. The product is built around execution rhythm: train, fuel, log what happened, and adjust the next session - all in one place. When your plan and fueling live together, the small daily habits that decide fitness are easier to repeat without juggling separate trackers.",
  },
  {
    question: "What is Bonk Prediction?",
    answer:
      "The Bonk Risk Index (BRI) is our proprietary algorithm that models your glycogen stores minute-by-minute during a ride. It predicts-before you even clip in-whether you'll run out of energy. If the risk is high, we adjust your fueling plan so you never hit the wall.",
  },
  {
    question: "How do I export workouts to supported platforms?",
    answer:
      "FuelCyclePro connects through Intervals.icu. Go to Settings > Connected Platforms > Connect Intervals.icu, paste your API key, and your workouts can sync to supported platforms like Garmin Connect, Zwift, MyWhoosh, Hammerhead Karoo, Bryton, and other compatible calendar or workout targets. You can also download .ZWO and .FIT files directly.",
  },
  {
    question: "How do I connect to Intervals.icu?",
    answer:
      "Intervals.icu is included on Free. Create an intervals.icu account, then in FuelCyclePro open Settings > Connected Platforms and connect with Intervals (OAuth preferred, or paste an API key). Link Garmin or Wahoo under Connections on intervals.icu. Full steps and connection terms: fuelcyclepro.com/intervals.",
  },
  {
    question: "Is my data safe?",
    answer:
      "Yes. FuelCyclePro is built by Hot Chilli Studios, a Swiss-based sole trader, and follows GDPR and Swiss FADP data protection standards. Your data is encrypted at rest and in transit. You can export or delete your data at any time from Settings.",
  },
];

const whyFuelCycleProItemsBase = [
  {
    title: "Bonk Risk Index",
    desc: "Minute-by-minute glycogen simulation predicts whether you'll run out of energy.",
  },
  {
    title: "Per-session fueling",
    desc: "Power-based carry lists and timing for this workout.",
  },
  {
    title: "Workout export",
    desc: "Structured sessions to Zwift, Garmin, MyWhoosh, Hammerhead, Bryton, ROUVY, and Intervals.icu.",
  },
];

/** Instrument elevation panels (no passive ring wrappers). */
const marketingFeatureCardClass = "instrument-fuel-tile";

/** Fixed mobile nav inner row is h-14 (3.5rem). Hero must clear that plus a gap.
 * `--consent-banner-offset` is set while the analytics banner sits under the nav. */
export const LANDING_HERO_PAD_CLASS =
  "pt-[calc(6rem+var(--consent-banner-offset,0px))] pb-6 sm:pt-[calc(7rem+var(--consent-banner-offset,0px))] sm:pb-10 lg:pt-[calc(8rem+var(--consent-banner-offset,0px))] lg:pb-12 px-4 sm:px-6";

function Landing() {
  const skipMotion = useInstantMotion();
  const [openFaq, setOpenFaq] = useState(null);
  /** Fail-closed: treat checkout as paused until pricing config confirms otherwise. */
  const [billingPaused, setBillingPaused] = useState(true);
  const [launchNote, setLaunchNote] = useState(
    "FuelCyclePro is free during launch with raised free limits. Pro plans and paid checkout will return later.",
  );
  const pricing = useMemo(() => {
    const { locale, currency } = resolveBrowserSubscriptionLocale();
    return getSubscriptionDisplayPricing(currency, locale);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPricingFreeFirstBundle()
      .then((bundle) => {
        if (cancelled) return;
        // Fail-closed: no pricing payload ⇒ keep checkout paused (shared cache may return open defaults).
        if (!bundle?.json) {
          setBillingPaused(true);
          return;
        }
        const paused = !!bundle.freeFirst?.billingCheckoutDisabled;
        setBillingPaused(paused);
        if (bundle.freeFirst?.messaging?.pricingNote) {
          setLaunchNote(String(bundle.freeFirst.messaging.pricingNote));
        } else if (bundle.freeFirst?.messaging?.bannerBody) {
          setLaunchNote(String(bundle.freeFirst.messaging.bannerBody));
        }
      })
      .catch(() => {
        if (!cancelled) setBillingPaused(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const faqItems = useMemo(
    () => [
      ...faqItemsBase,
      {
        question: "How much does it cost?",
        answer: billingPaused ? FAQ_COST_PAUSED : FAQ_COST_OPEN,
      },
    ],
    [billingPaused],
  );

  const whyFuelCycleProItems = useMemo(
    () => [
      ...whyFuelCycleProItemsBase,
      billingPaused
        ? {
            title: "One app, launch access",
            desc: "Training, nutrition, and execution in one workflow.",
          }
        : {
            title: "One app, one price",
            desc: `Training, nutrition, and execution in one workflow from ${pricing.monthlyFormatted}/mo - no stacking two subscriptions.`,
          },
    ],
    [billingPaused, pricing.monthlyFormatted],
  );

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Track CTA clicks via PostHog
  const handleCtaClick = useCallback((ctaName, destination) => {
    trackEvent("landing_cta_click", {
      cta: ctaName,
      destination,
      timestamp: new Date().toISOString(),
    });
  }, []);

  // Track section visibility for scroll depth analytics
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent("landing_section_viewed", {
              section: entry.target.dataset.section,
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = document.querySelectorAll("[data-section]");
    sections.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`relative ${WWW_PAGE_SHELL_MIN_H_CLASS} bg-[var(--bg-primary)]`}>
      {/* Hero — Mobile Responsive */}
      <section
        data-section="hero"
        className={LANDING_HERO_PAD_CLASS}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center">
            <motion.div
              initial={wwwRouteEnter(skipMotion, 24)}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-3 sm:space-y-8 text-center lg:text-left"
            >
              <h1 className="font-display text-[clamp(1.625rem,5vw+1rem,2.25rem)] sm:text-5xl lg:text-7xl font-extrabold text-stone-900 dark:text-white leading-[1.1] sm:leading-[1.05] tracking-tight">
                Will This Ride
                <br />
                <span className="text-primary-500">Empty You?</span>
              </h1>
              <div className="space-y-3 text-sm sm:text-xl text-stone-600 dark:text-[var(--text-secondary)] max-w-xl mx-auto lg:mx-0 leading-relaxed">
                <p>
                  Other platforms make you juggle two subscriptions. FuelCyclePro
                  was built to combine training and nutrition. Your training plan
                  drives your nutrition plan, and that nutrition state predicts
                  your bonk risk.
                </p>
                <p>
                  Other apps may tell you what to eat. FuelCyclePro tells you
                  what happens if you don&apos;t.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link
                  to="/register"
                  onClick={() =>
                    handleCtaClick(
                      billingPaused ? "hero-get-started-free" : "hero-start-trial",
                      "/register",
                    )
                  }
                  className={LANDING_PRIMARY_CTA_CLASS}
                  data-cta={
                    billingPaused ? "hero-get-started-free" : "hero-start-trial"
                  }
                >
                  {billingPaused ? "Get started free" : "Start Free Trial"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  onClick={() =>
                    handleCtaClick(LANDING_HERO_PRICING_CTA, "/pricing")
                  }
                  className={LANDING_SECONDARY_CTA_CLASS}
                  data-cta={LANDING_HERO_PRICING_CTA}
                >
                  {LANDING_PRICING_CTA_LABEL}
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={skipMotion ? false : { opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden md:block mt-8 lg:mt-0"
            >
              <MarketingInstrumentDemo variant="hero" className="w-full max-w-xl lg:ml-auto" />
            </motion.div>
          </div>
        </div>
      </section>

      <LandingProductShots />

      {/* Features — bento grid, balanced 3+2 on large screens */}
      <section
        id="features"
        data-section="features"
        className={LANDING_SURFACE_BAND_TIGHT_CLASS}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.2]"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 80% 50% at 50% -20%, var(--brand-glow), transparent), radial-gradient(ellipse 60% 40% at 100% 50%, var(--brand-glow), transparent)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={wwwRouteEnter(skipMotion, 16)}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10 sm:mb-14"
          >
            <LandingSectionHeading
              lead="Session-scoped fueling."
              subtitle="Every workout shows you fuel targets, risk flags, and on-cycle carry list."
            />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-5 lg:gap-6">
            {[
              {
                title: "Per-Ride Fueling",
                desc: "Exactly what to eat before, during, and after each workout. No more guessing.",
              },
              {
                title: "Bonk Risk Index",
                desc: "Minute-by-minute glycogen simulation predicts whether you'll run out of energy.",
              },
              {
                title: "Training-Adjusted Calories",
                desc: "More on hard days. Less on rest days. Automatically calculated.",
              },
              {
                title: "Intervals.icu sync",
                desc: "Push plans and sync rides through Intervals.icu, then download .ZWO and .FIT files for your trainer or head unit.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={wwwRouteEnter(skipMotion, 20)}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, type: "spring", stiffness: 260, damping: 26 }}
                className="group relative flex flex-col h-full min-h-0 instrument-fuel-tile transition-all duration-300 ease-out hover:-translate-y-1 lg:col-span-3"
              >
                <MarketingIcon />
                <h3 className="font-display font-semibold text-base sm:text-lg text-stone-900 dark:text-white mb-2 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-[15px] text-stone-600 dark:text-[var(--text-secondary)]/95 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We're Different — Technical Differentiation */}
      <section
        data-section="differentiation"
        className={LANDING_SURFACE_BAND_RAISED_CLASS}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <LandingSectionHeading
              lead="Predictive fueling"
              subtitle="We don't just suggest macros. We turn each workout into a ride execution plan with power-based fueling, hydration, and bonk prevention."
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Differentiator 1 */}
            <div className={marketingFeatureCardClass}>
              <MarketingIcon />
              <h3 className="font-display font-bold text-lg text-stone-900 dark:text-white mb-2">
                Power-Based, Not HR-Based
              </h3>
              <p className="text-stone-600 dark:text-[var(--text-secondary)] text-sm">
                We calculate energy from your{" "}
                <strong>actual power output</strong> (watts), not heart rate
                estimates. More accurate, more specific.
              </p>
              <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
                <p className="text-xs text-stone-500 dark:text-[var(--text-muted)]">
                  <span className="text-red-500">Others:</span> Estimate from
                  heart rate (±30% error)
                  <br />
                  <span className="text-green-600 dark:text-green-400">
                    Us:
                  </span>{" "}
                  Calculate from power (±5% error)
                </p>
              </div>
            </div>

            {/* Differentiator 2 */}
            <div className={marketingFeatureCardClass}>
              <MarketingIcon />
              <h3 className="font-display font-bold text-lg text-stone-900 dark:text-white mb-2">
                Before and After the Ride
              </h3>
              <p className="text-stone-600 dark:text-[var(--text-secondary)] text-sm">
                We warn you before the ride, then review what likely slipped after
                it. That means fewer bonks and faster next-session corrections.
              </p>
              <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
                <p className="text-xs text-stone-500 dark:text-[var(--text-muted)]">
                  <span className="text-red-500">CGM:</span> Alerts when you&apos;re
                  already low ($200+ hardware)
                  <br />
                  <span className="text-green-600 dark:text-green-400">
                    Us:
                  </span>{" "}
                  Predicts hours ahead (no hardware needed)
                </p>
              </div>
            </div>

            {/* Differentiator 3 */}
            <div className={marketingFeatureCardClass}>
              <MarketingIcon />
              <h3 className="font-display font-bold text-lg text-stone-900 dark:text-white mb-2">
                Carry and Timing Guidance
              </h3>
              <p className="text-stone-600 dark:text-[var(--text-secondary)] text-sm">
                Not generic &quot;eat 60g/hour.&quot; We tell you:
                <strong> what to carry, when to start, and how to hold the plan</strong>
                {" "}for this exact ride.
              </p>
              <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
                <p className="text-xs text-stone-500 dark:text-[var(--text-muted)]">
                  <span className="text-red-500">Others:</span> Generic advice
                  for any workout
                  <br />
                  <span className="text-green-600 dark:text-green-400">
                    Us:
                  </span>{" "}
                  Specific prescription per session
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why FuelCyclePro */}
      <section
        data-section="why-fuelcyclepro"
        className={LANDING_SURFACE_BAND_CLASS}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <LandingSectionHeading
              lead="Why"
              accent="FuelCyclePro?"
              subtitle="Training, per-ride fueling, and exports in one specialist workflow - built for cyclists who train hard."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {whyFuelCycleProItems.map((item, i) => (
              <motion.div
                key={item.title}
                initial={wwwRouteEnter(skipMotion, 16)}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={marketingFeatureCardClass}
              >
                <MarketingIcon />
                <h3 className="font-display font-semibold text-base sm:text-lg text-stone-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-stone-600 dark:text-[var(--text-secondary)] leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-section="faq"
        className={LANDING_SURFACE_BAND_RAISED_CLASS}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={wwwRouteEnter(skipMotion, 16)}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 sm:mb-12"
          >
            <LandingSectionHeading
              lead="Frequently asked"
              accent="questions"
              subtitle="Everything you need to know to get started."
            />
          </motion.div>

          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={wwwRouteEnter(skipMotion, 8)}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`${marketingFeatureCardClass} overflow-hidden p-0`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-stone-50/80 dark:hover:bg-stone-800/80 transition-colors"
                >
                  <span className="font-semibold text-sm sm:text-base text-stone-900 dark:text-white pr-4">
                    {item.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className="w-5 h-5 text-stone-400" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-5 pb-4 text-sm sm:text-base text-stone-600 dark:text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Mobile Responsive */}
      <section
        data-section="bottom-cta"
        className="py-12 sm:py-24 bg-stone-900"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="mb-6 sm:mb-10">
            <LandingSectionHeading
              lead="Know before you ride."
              accent="Fuel with precision."
              subtitle={
                billingPaused
                  ? launchNote
                  : "7-day free trial. No credit card required. Cancel anytime."
              }
              onDark
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/register"
              onClick={() =>
                handleCtaClick(
                  billingPaused ? "bottom-get-started-free" : "bottom-start-trial",
                  "/register",
                )
              }
              className={LANDING_PRIMARY_CTA_CLASS}
              data-cta={
                billingPaused ? "bottom-get-started-free" : "bottom-start-trial"
              }
            >
              {billingPaused ? "Get started free" : "Start Free Trial"}
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <Link
              to="/pricing"
              onClick={() =>
                handleCtaClick(LANDING_BOTTOM_PRICING_CTA, "/pricing")
              }
              className={`${LANDING_SECONDARY_CTA_CLASS} btn-secondary-ghost-on-dark`}
              data-cta={LANDING_BOTTOM_PRICING_CTA}
            >
              {LANDING_PRICING_CTA_LABEL}
            </Link>
          </div>
        </div>
      </section>

      <MarketingSiteFooter />
    </div>
  );
}

function MarketingIcon() {
  return (
    <span
      className="mb-4 block h-1.5 w-10 rounded-full bg-primary-500/75 shadow-[0_0_14px_rgba(179,130,15,0.18)]"
      aria-hidden
    />
  );
}

export default Landing;
